import { Controller } from "@hotwired/stimulus"

// Both roleta screens render the same chip (data-manual), only for the rotations
// a user belongs to: the click toggles inactive <-> active and the X leaves the
// rotation. Which screen a chip talks to comes from data-rotation-resource.
const CHIP_CYCLE = {
  inactive: 'activate',
  active: 'deactivate'
}

const CHIP_CLASSES = {
  inactive: 'bg-amber-200 text-amber-800 border border-amber-500',
  active: 'bg-green-200 text-green-800 border border-green-500',
  active_offline: 'bg-green-200 text-green-800 border border-dashed border-green-500'
}

const STATE_CLASSES = {
  none: 'bg-gray-200 text-gray-500 hover:bg-gray-300',
  inactive: 'bg-amber-200 text-amber-800 border border-amber-500 hover:opacity-80',
  active: 'bg-green-200 text-green-800 border border-green-500 hover:opacity-80',
  active_offline: 'bg-green-200 text-green-800 border border-dashed border-green-500 hover:opacity-80'
}

const CHIP_BASE = 'inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded text-xs font-medium transition-all duration-200 select-none'
const LEGACY_BASE = 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-all duration-200 select-none active:scale-95'

export default class extends Controller {
  static targets = [
    "rotationBadge", "badgeToggle", "badgeText", "badgeSpinner", "userHistoryModal",
    "emptyLabel", "addModalUser", "addModalSearch", "addModalList", "addModalOption", "addModalEmpty"
  ]

  async toggleRotation(event) {
    const button = event.currentTarget
    const { userId, rotationId, rotationName, realEstateAdminId, roleSourced } = button.dataset

    if (roleSourced === 'true') {
      this.showToast('Roleta gerenciada por perfil. Não é possível alterar manualmente.', 'error')
      return
    }

    const badge = this.badgeRoot(button)
    const currentState = badge?.dataset.state || 'none'
    const actionStep = CHIP_CYCLE[currentState]
    if (!actionStep) return

    this.setButtonLoading(button, true)

    const data = await this.postAssignment({ userId, rotationId, realEstateAdminId, actionStep, button })

    if (data) {
      this.updateBadgeState(badge, data.state, rotationName, data.offline)
      this.updateRotationCardCounts(rotationId, data.state, currentState)
      this.showToast(data.message, 'success')
    }

    this.setButtonLoading(button, false)
  }

  async removeFromRotation(event) {
    event.stopPropagation()

    const button = event.currentTarget
    const { userId, rotationId, realEstateAdminId } = button.dataset
    const badge = this.badgeRoot(button)
    const previousState = badge?.dataset.state || 'inactive'

    button.classList.add('opacity-40', 'pointer-events-none')

    const data = await this.postAssignment({ userId, rotationId, realEstateAdminId, actionStep: 'remove', button })

    if (data) {
      badge?.remove()
      this.updateRotationCardCounts(rotationId, 'none', previousState)
      this.refreshUserRow(userId)
      this.showToast(data.message, 'success')
    } else {
      button.classList.remove('opacity-40', 'pointer-events-none')
    }
  }

  // Never defaulted: guessing the resource here would let one screen write to the
  // other's rotations, so a missing attribute has to fail loudly instead.
  async postAssignment({ userId, rotationId, realEstateAdminId, actionStep, button }) {
    const rotationResource = button?.dataset.rotationResource

    if (!rotationResource) {
      console.error('rotation-assignment: element has no data-rotation-resource', button)
      this.showToast('Não foi possível identificar a roleta. Recarregue a página.', 'error')
      return null
    }

    try {
      const response = await fetch(`/real_estate_admin/${realEstateAdminId}/${rotationResource}/${rotationId}/toggle_user_roleta`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ user_id: userId, action_step: actionStep })
      })

      const data = await response.json()

      if (response.ok && data.status === 'success') return data

      this.showToast(data.message || 'Erro ao atualizar atribuição.', 'error')
    } catch (error) {
      console.error('Error updating rotation assignment:', error)
      this.showToast('Erro de conexão. Tente novamente.', 'error')
    }

    return null
  }

  badgeRoot(element) {
    return element.closest('[data-state]')
  }

  rotationsContainer(userId) {
    return document.getElementById(`user-${userId}-rotations-container`)
  }

  refreshUserRow(userId) {
    const container = this.rotationsContainer(userId)
    if (!container) return

    const hasBadges = container.querySelector('[id^="user-"][data-state]') !== null

    container.querySelector('[data-rotation-assignment-target="emptyLabel"]')?.classList.toggle('hidden', hasBadges)
    document.getElementById(`user-${userId}-bulk-empty`)?.classList.toggle('hidden', hasBadges)
    document.getElementById(`user-${userId}-bulk-buttons`)?.classList.toggle('hidden', !hasBadges)
  }

  openAddRotation(event) {
    const { userId, userName } = event.currentTarget.dataset
    const modal = document.getElementById('add_rotation_modal')
    if (!modal) return

    this.pendingUserId = userId

    if (this.hasAddModalUserTarget) this.addModalUserTarget.textContent = userName || ''
    if (this.hasAddModalSearchTarget) this.addModalSearchTarget.value = ''

    const container = this.rotationsContainer(userId)
    const assigned = new Set(
      Array.from(container?.querySelectorAll('[data-state]') || [])
        .map((badge) => badge.id.replace(`user-${userId}-rotation-`, ''))
    )

    let available = 0
    this.addModalOptionTargets.forEach((option) => {
      const alreadyIn = assigned.has(option.dataset.rotationId)
      option.dataset.excluded = alreadyIn ? 'true' : 'false'
      option.classList.toggle('hidden', alreadyIn)
      if (!alreadyIn) available += 1
    })

    if (this.hasAddModalEmptyTarget) this.addModalEmptyTarget.classList.toggle('hidden', available > 0)

    modal.showModal()
  }

  closeAddRotation() {
    document.getElementById('add_rotation_modal')?.close()
  }

  filterAddRotationOptions() {
    const term = (this.addModalSearchTarget.value || '').toLowerCase().trim()

    this.addModalOptionTargets.forEach((option) => {
      if (option.dataset.excluded === 'true') return

      const matches = option.dataset.rotationName.toLowerCase().includes(term)
      option.classList.toggle('hidden', !matches)
    })
  }

  async confirmAddRotation(event) {
    const button = event.currentTarget
    const { rotationId, rotationName, realEstateAdminId, rotationResource } = button.dataset
    const userId = this.pendingUserId
    if (!userId) return

    button.classList.add('opacity-50', 'pointer-events-none')

    const data = await this.postAssignment({ userId, rotationId, realEstateAdminId, actionStep: 'add', button })

    if (data) {
      this.insertBadge({ userId, rotationId, rotationName, realEstateAdminId, rotationResource, state: data.state })
      this.updateRotationCardCounts(rotationId, data.state, 'none')
      this.refreshUserRow(userId)
      this.showToast(data.message, 'success')
      this.closeAddRotation()
    }

    button.classList.remove('opacity-50', 'pointer-events-none')
  }

  insertBadge({ userId, rotationId, rotationName, realEstateAdminId, rotationResource, state }) {
    const container = this.rotationsContainer(userId)
    if (!container || document.getElementById(`user-${userId}-rotation-${rotationId}`)) return

    const badge = document.createElement('span')
    badge.id = `user-${userId}-rotation-${rotationId}`
    badge.className = `${CHIP_BASE} ${CHIP_CLASSES[state] || CHIP_CLASSES.inactive}`
    badge.dataset.state = state
    badge.dataset.manual = 'true'
    badge.dataset.rotationAssignmentTarget = 'rotationBadge'

    const applyDataset = (element, action) => {
      element.type = 'button'
      element.dataset.action = `click->rotation-assignment#${action}`
      element.dataset.userId = userId
      element.dataset.rotationId = rotationId
      element.dataset.rotationName = rotationName
      element.dataset.rotationResource = rotationResource
      element.dataset.realEstateAdminId = realEstateAdminId
    }

    const toggle = document.createElement('button')
    applyDataset(toggle, 'toggleRotation')
    toggle.className = 'inline-flex items-center gap-1 cursor-pointer hover:opacity-80 active:scale-95'
    toggle.title = `Ativar em ${rotationName}`
    toggle.dataset.rotationAssignmentTarget = 'badgeToggle'

    const text = document.createElement('span')
    text.dataset.rotationAssignmentTarget = 'badgeText'
    text.textContent = rotationName

    const spinner = document.createElement('span')
    spinner.className = 'loading loading-spinner loading-xs hidden'
    spinner.dataset.rotationAssignmentTarget = 'badgeSpinner'

    toggle.append(text, spinner)

    const remove = document.createElement('button')
    applyDataset(remove, 'removeFromRotation')
    remove.className = 'inline-flex items-center leading-none opacity-50 hover:opacity-100 hover:text-red-700 transition-opacity cursor-pointer'
    remove.title = `Remover de ${rotationName}`
    remove.innerHTML = '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>'

    badge.append(toggle, remove)

    container.insertBefore(badge, container.querySelector('[data-action*="openAddRotation"]'))
  }

  async toggleAllRotations(event) {
    const button = event.currentTarget
    const { userId, available, url } = button.dataset
    const activate = available === 'true'

    button.classList.add('opacity-75', 'pointer-events-none')

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ user_id: userId, available: activate })
      })

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        document.querySelectorAll(`[id^="user-${userId}-rotation-"]`).forEach(badge => {
          const state = badge.dataset.state
          if (!state || state === 'none') return

          const newState = activate ? 'active' : 'inactive'
          const rotationName = badge.querySelector('[data-rotation-assignment-target="badgeText"]')?.textContent || ''
          const rotationId = badge.id.replace(`user-${userId}-rotation-`, '')

          if (badge.dataset.manual === 'true' || badge.tagName === 'BUTTON') {
            this.updateBadgeState(badge, newState, rotationName, false)
          } else {
            // Role/group sourced badges are plain, non-interactive chips
            badge.dataset.state = newState
            const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium select-none'
            badge.className = `${base} ${STATE_CLASSES[newState]}`
            badge.querySelector('svg.checkmark-icon')?.remove()
            badge.querySelector('.online-label')?.remove()
          }

          this.updateRotationCardCounts(rotationId, newState, state)
        })
        this.showToast(data.message, 'success')
      } else {
        this.showToast(data.message || 'Erro ao atualizar disponibilidade.', 'error')
      }
    } catch (error) {
      console.error('Error toggling all rotations:', error)
      this.showToast('Erro de conexão. Tente novamente.', 'error')
    } finally {
      button.classList.remove('opacity-75', 'pointer-events-none')
    }
  }

  async showUserHistory(event) {
    const { url, userName, userId, realEstateAdminId, rotationResource } = event.currentTarget.dataset
    const modal = document.getElementById('user_history_modal_shared')
    const title = document.getElementById('user_history_modal_title')
    const body = document.getElementById('user_history_modal_body')
    const exportLink = document.getElementById('user_history_export_link')

    title.textContent = `Atribuições de ${userName}`
    if (exportLink) {
      exportLink.classList.toggle('hidden', !rotationResource)
      if (rotationResource) {
        exportLink.href = `/real_estate_admin/${realEstateAdminId}/${rotationResource}/export_history?user_id=${userId}`
      }
    }
    body.innerHTML = '<div class="px-4 py-8 text-center"><span class="loading loading-spinner loading-md text-gray-400"></span></div>'
    modal.showModal()

    try {
      const response = await fetch(url, {
        headers: { 'X-CSRF-Token': this.csrfToken, Accept: 'text/html' }
      })
      body.innerHTML = response.ok ? await response.text() : '<div class="px-4 py-8 text-center text-sm text-red-500">Erro ao carregar histórico.</div>'
    } catch {
      body.innerHTML = '<div class="px-4 py-8 text-center text-sm text-red-500">Erro de conexão.</div>'
    }
  }

  updateRotationCardCounts(rotationId, newState, oldState) {
    const activeCountEl = document.querySelector(`[data-rotation-active-count="${rotationId}"]`)
    const totalCountEl = document.querySelector(`[data-rotation-total-count="${rotationId}"]`)

    if (activeCountEl) {
      const currentActive = parseInt(activeCountEl.textContent) || 0
      let delta = 0
      // Active count changes when entering or leaving 'active' state
      if (newState === 'active' && oldState !== 'active') delta = 1
      if (oldState === 'active' && newState !== 'active') delta = -1
      activeCountEl.textContent = Math.max(0, currentActive + delta)
    }

    if (totalCountEl) {
      const match = totalCountEl.textContent.match(/(\d+)/)
      const currentTotal = match ? parseInt(match[1]) : 0
      let delta = 0
      if (newState !== 'none' && oldState === 'none') delta = 1
      if (newState === 'none' && oldState !== 'none') delta = -1
      totalCountEl.textContent = `${Math.max(0, currentTotal + delta)} usuários`
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  setButtonLoading(button, loading) {
    const spinner = button.querySelector('[data-rotation-assignment-target="badgeSpinner"]')

    if (loading) {
      button.classList.add('opacity-75', 'pointer-events-none')
      if (spinner) spinner.classList.remove('hidden')
    } else {
      button.classList.remove('opacity-75', 'pointer-events-none')
      if (spinner) spinner.classList.add('hidden')
    }
  }

  updateBadgeState(badge, newState, rotationName, offline = false) {
    if (!badge) return

    const isChip = badge.dataset.manual === 'true'
    const toggle = isChip ? badge.querySelector('[data-rotation-assignment-target="badgeToggle"]') : badge
    if (!toggle) return

    badge.dataset.state = newState

    const visualState = (newState === 'active' && offline) ? 'active_offline' : newState
    badge.removeAttribute('style')
    badge.className = isChip
      ? `${CHIP_BASE} ${CHIP_CLASSES[visualState]}`
      : `${LEGACY_BASE} ${STATE_CLASSES[visualState]}`

    // Remove existing checkmark and online label
    toggle.querySelector('svg.checkmark-icon')?.remove()
    toggle.querySelector('.online-label')?.remove()

    if (newState === 'active') {
      const checkIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      checkIcon.setAttribute('class', 'w-3 h-3 checkmark-icon')
      checkIcon.setAttribute('fill', 'none')
      checkIcon.setAttribute('stroke', 'currentColor')
      checkIcon.setAttribute('viewBox', '0 0 24 24')
      checkIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>'
      toggle.insertBefore(checkIcon, toggle.firstChild)

      if (offline !== undefined) {
        const badgeText = toggle.querySelector('[data-rotation-assignment-target="badgeText"]')
        const label = document.createElement('span')
        label.className = 'text-[9px] opacity-70 online-label'
        label.textContent = offline ? '(offline)' : '(online)'
        badgeText?.after(label)
      }
    }

    const chipTitles = { inactive: `Ativar em ${rotationName}`, active: `Desativar em ${rotationName}` }
    const legacyTitles = { none: `Adicionar a ${rotationName}`, inactive: `Ativar em ${rotationName}`, active: `Remover de ${rotationName}` }
    toggle.title = isChip ? chipTitles[newState] : legacyTitles[newState]

    // Brief success animation
    badge.classList.add('ring-2', 'ring-green-400')
    setTimeout(() => badge.classList.remove('ring-2', 'ring-green-400'), 500)
  }

  showToast(message, type = 'info') {
    document.querySelectorAll('.rotation-toast').forEach(t => t.remove())

    const toast = document.createElement('div')
    toast.className = `rotation-toast fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-2 transform translate-y-2 opacity-0 transition-all duration-300`

    if (type === 'success') {
      toast.classList.add('bg-green-600', 'text-white')
      toast.innerHTML = `
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span class="font-medium">${message}</span>
      `
    } else if (type === 'error') {
      toast.classList.add('bg-red-600', 'text-white')
      toast.innerHTML = `
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <span class="font-medium">${message}</span>
      `
    } else {
      toast.classList.add('bg-gray-800', 'text-white')
      toast.innerHTML = `<span class="font-medium">${message}</span>`
    }

    document.body.appendChild(toast)

    requestAnimationFrame(() => {
      toast.classList.remove('translate-y-2', 'opacity-0')
    })

    setTimeout(() => {
      toast.classList.add('translate-y-2', 'opacity-0')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
